import update, { extend } from 'immutability-helper';
import {
  FETCH_ERROR,
  FETCH_REQUEST,
  FETCH_SUCCESS,
} from '../../sharedActionTypes';
import { FORCED_FETCH } from '../../fetchModes';

extend('$filterRemove', (unwanted, original) =>
  original.filter((x) => !unwanted.includes(x))
);
extend('$dec', (y, x) => x - y);
extend('$inc', (y, x) => x + y);

const defaultDefaultReducer = (state) => state;

/**
 *
 * @param initialState
 * @param fetchRequestReducer: Function
 * @param fetchSuccessReducer: Function
 * @param fetchErrorReducer: Function
 * @param defaultReducer: Function
 * @return {Function}
 */
const withFetch =
  (
    initialState,
    fetchRequestReducer,
    fetchSuccessReducer,
    fetchErrorReducer,
    defaultReducer = defaultDefaultReducer
  ) =>
  (state = initialState, action) => {
    switch (action.type) {
      case FETCH_REQUEST:
        return fetchRequestReducer(state, action);
      case FETCH_SUCCESS:
        return fetchSuccessReducer(state, action);
      case FETCH_ERROR:
        return fetchErrorReducer(state, action);
      default:
        return defaultReducer(state, action);
    }
  };

export default withFetch;

const defaultActionDataToId = (data) => data.id;
const defaultResultMapper = (data) => data;

export const createDefaultRequestInitialState = (
  baseInitialState = undefined
) => ({
  ...baseInitialState,
  byId: {},
  loading: 0,
  loaded: 0,
  error: 0,
});

const defaultExtraOnSuccess = (state, itemId) => state;

export const createInitialItemState = () => ({
  loading: false,
  loaded: false,
  error: null,
  result: null,
});

export const defaultRequestByIdReducer = ({
  actionDataToId = defaultActionDataToId,
  resultMapper = defaultResultMapper,
  baseInitialState = undefined,
  extraOnSuccess = defaultExtraOnSuccess,
} = {}) => {
  const initialState = createDefaultRequestInitialState(baseInitialState);
  const fetchRequestReducer = (prevState, action) => {
    const itemId = actionDataToId(action.data);
    const prevItemState = prevState.byId[itemId] || createInitialItemState();
    const nextItemState = {
      ...prevItemState,
      loading: true,
      loaded: action.mode === FORCED_FETCH ? false : prevItemState.loaded,
      result: action.mode === FORCED_FETCH ? null : prevItemState.result,
      error: null,
    };
    return update(prevState, {
      loading: { $inc: prevItemState.loading ? 0 : 1 },
      loaded: { $dec: prevItemState.loaded ? 1 : 0 },
      error: { $dec: prevItemState.error ? 1 : 0 },
      byId: {
        [itemId]: { $set: nextItemState },
      },
    });
  };
  const fetchSuccessReducer = (prevState, action) => {
    const itemId = actionDataToId(action.reqData);
    const prevItemState = prevState.byId[itemId];
    const nextItemState = {
      ...prevItemState,
      loading: false,
      loaded: true,
      error: null,
      result: resultMapper(action.data),
    };

    if (
      prevItemState &&
      prevItemState.result &&
      prevItemState.result.submissions
    ) {
      let refSubmissions = {
        complete: [
          ...prevItemState.result.submissions.complete,
          ...nextItemState.result.submissions.complete,
        ],
        incomplete: [
          ...prevItemState.result.submissions.incomplete,
          ...nextItemState.result.submissions.incomplete,
        ],
      };

      const getUniqueListBy = (arr, key) => {
        return [...new Map(arr.map((item) => [item[key], item])).values()];
      };
      const arr1 = getUniqueListBy(refSubmissions.complete, 'id');
      let arr2 = getUniqueListBy(refSubmissions.incomplete, 'id');
      for (const e of arr1) {
        arr2 = arr2.filter((el) => !(el.id === e.id)).length > 0 ? arr2.filter((el) => !(el.id === e.id)) : arr2;
      }
      refSubmissions.complete = arr1;
      refSubmissions.incomplete = arr2;

      return extraOnSuccess(
        update(prevState, {
          loading: { $dec: prevItemState.loading ? 1 : 0 },
          loaded: { $inc: prevItemState.loaded ? 0 : 1 },
          error: { $dec: prevItemState.error ? 1 : 0 },
          byId: {
            [itemId]: {
              result: {
                submissions: { $set: refSubmissions },
              },
            },
          },
        }),
        itemId
      );
      // return extraOnSuccess(
      //   update(prevState, {
      //     loading: { $dec: prevItemState.loading ? 1 : 0 },
      //     loaded: { $inc: prevItemState.loaded ? 0 : 1 },
      //     error: { $dec: prevItemState.error ? 1 : 0 },
      //     byId: {
      //       [itemId]: {
      //         result: {
      //           submissions: { $push: nextItemState.result.submissions },
      //         },
      //       },
      //     },
      //   }),
      //   itemId
      // );
    } else if (
      prevItemState &&
      prevItemState.result &&
      prevItemState.result.timingsById
    ) {
      // console.log('prev state', prevItemState);
      // console.log('nextItemState', nextItemState);
      return extraOnSuccess(
        update(prevState, {
          loading: { $dec: prevItemState.loading ? 1 : 0 },
          loaded: { $inc: prevItemState.loaded ? 0 : 1 },
          error: { $dec: prevItemState.error ? 1 : 0 },
          byId: {
            [itemId]: {
              result: {
                timingsById: { $merge: nextItemState.result.timingsById },
              },
            },
          },
        }),
        itemId
      );
    }

    return extraOnSuccess(
      update(prevState, {
        loading: { $dec: prevItemState.loading ? 1 : 0 },
        loaded: { $inc: prevItemState.loaded ? 0 : 1 },
        error: { $dec: prevItemState.error ? 1 : 0 },
        byId: {
          [itemId]: { $set: nextItemState },
        },
      }),
      itemId
    );
  };
  const fetchErrorReducer = (prevState, action) => {
    const itemId = actionDataToId(action.data);
    const prevItemState = prevState.byId[itemId];
    const nextItemState = {
      ...prevItemState,
      loading: false,
      error: action.error,
    };
    return update(prevState, {
      loading: { $dec: prevItemState.loading ? 1 : 0 },
      error: { $inc: prevItemState.error ? 0 : 1 },
      byId: {
        [itemId]: { $set: nextItemState },
      },
    });
  };
  return withFetch(
    initialState,
    fetchRequestReducer,
    fetchSuccessReducer,
    fetchErrorReducer
  );
};

export const createSelectorById =
  (defaultSelectState) =>
  (id, selectState = defaultSelectState) =>
  (state) =>
    selectState(state).byId[id] || createInitialItemState();
export const createSelectorByIdList =
  (defaultSelectState) =>
  (idList, selectState = defaultSelectState) =>
  (state) =>
    idList.map((id) => selectState(state).byId[id] || createInitialItemState());
